import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { Collaborator } from '@/types/collaborations';

interface CollaboratorDialogContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  editingCollaborator: Collaborator | null;
  setEditingCollaborator: (collaborator: Collaborator | null) => void;
  openDialog: (collaborator?: Collaborator) => void;
  closeDialog: () => void;
}

const CollaboratorDialogContext = createContext<CollaboratorDialogContextType | undefined>(undefined);

export const CollaboratorDialogProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingCollaborator, setEditingCollaborator] = useState<Collaborator | null>(null);

  const openDialog = (collaborator?: Collaborator) => {
    if (collaborator) {
      setEditingCollaborator(collaborator);
    } else {
      setEditingCollaborator(null);
    }
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    // Don't clear editingCollaborator immediately to allow smooth transition
    setTimeout(() => setEditingCollaborator(null), 300);
  };

  return (
    <CollaboratorDialogContext.Provider
      value={{
        isOpen,
        setIsOpen,
        editingCollaborator,
        setEditingCollaborator,
        openDialog,
        closeDialog,
      }}
    >
      {children}
    </CollaboratorDialogContext.Provider>
  );
};

export const useCollaboratorDialog = () => {
  const context = useContext(CollaboratorDialogContext);
  if (!context) {
    throw new Error('useCollaboratorDialog must be used within CollaboratorDialogProvider');
  }
  return context;
};
